/* eslint-disable no-unreachable */
/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import 'styled-components/macro';
import { AuthContext } from 'context/authContext';
import Button from 'components/atoms/Button';
import Grid from 'components/atoms/Grid';
import Field from 'components/molecules/Field';
import Form, { useForm } from 'components/molecules/Form';
import Toast from 'components/molecules/Toast';
import { useContextHook } from 'use-context-hook';
import resellerService from 'services/resellers.service';
import { useTranslation } from 'helpers/useTranslation';
import TableLayout from 'components/atoms/TableLayout';
import { usePermissions } from 'helpers/usePermissions';
import { format } from 'date-fns';
import { convertToCurrencyFormat, getComissionAmount, getDateObject, LangConverter } from 'helpers/common';
import ModalContainer from 'components/molecules/ModalContainer';
import Tooltip from 'components/atoms/Tooltip';
import Table from 'components/molecules/Table';
import { NoRecordFound } from 'components/molecules/Table/Table.styles';
import Icon from 'components/atoms/Icon';
import Paragraph from 'components/atoms/Paragraph';
import { OrderStatus } from '../OrderStatus';
import PdfViewer from '../PdfViewer';

function UploadBulkPaymentProof({ onClose = () => {}, order, paymentCommitment = false }) {
  const [form] = useForm();
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(false);
  const { refetch } = useContextHook(AuthContext, ['refetch']);
  const { t } = useTranslation();
  const { lang, user } = useContextHook(AuthContext, { lang: 1, user: 1 });
  const { hasPermission } = usePermissions();

  const [searchQuery, setSearchQuery] = useState({
    page: 1,
    pageSize: 10,
    searchText: '',
    startDate: '',
    endDate: '',
    filterText: '',
  });
  const { fetch } = useContextHook(AuthContext, ['fetch']);

  const { dashboard_data, dashboard_loading } = resellerService.GetResellerDashboard(searchQuery, fetch);

  useEffect(() => {
    if (order) {
      const services = order.servicesDetail?.map(itm => {
        form.setFieldsValue({ [`units_${itm?.serviceId?._id}`]: itm?.units });
        const filteredPackage = order?.packagesDetail?.find(pckg => pckg?.serviceId === itm?.serviceId?._id)?.packageId;
        return {
          label: !itm?.packageId
            ? `${t('Service Code')}: ${itm?.serviceId?.serviceCode} ${t('Provider')}:${
                itm?.serviceId?.providerId?.providerName
              }`
            : `${t('Package Name')}: ${filteredPackage?.name} ${t('Service Code')}: ${itm?.serviceId?.serviceCode} ${t(
                'Provider',
              )}:${itm?.serviceId?.providerId?.providerName}`,
          value: !itm?.packageId ? itm?.serviceId?._id : itm?.packageId,
          serviceCode: itm?.serviceId?.serviceCode,
          price: itm?.packageId ? filteredPackage?.packagePrice : itm?.serviceId?.price,
          discountedAmount:
            itm?.originalAmount / itm?.units - ((itm?.originalAmount / itm?.units) * itm?.discount) / 100,
          units: itm?.units,
          totalPrice: itm?.amount,
          package: itm?.packageId,
          serviceId: itm?.serviceId?._id,
        };
      });
      const s = {
        serviceId: services,
        paymentProof: order?.paymentProof ? order?.paymentProof : '',
        paymentCommitment: order?.paymentCommitment ?? '',
      };
      form.setFieldsValue(s);
      setState(prev => ({ ...prev, ...s }));
    }
  }, [order]);

  const onSubmit = async () => {
    if (!searchQuery?.startDate && !searchQuery?.endDate) {
      Toast({
        type: 'error',
        message: 'Please select a date range to upload payment proof',
      });
      return;
    }
    const payload = {
      paymentProof: state?.paymentProof,
      startDate: searchQuery?.startDate,
      endDate: searchQuery?.endDate,
      isBulkPaymentProof: true,
    };
    try {
      setLoading(true);
      const res = await resellerService.updateOrder(null, payload);
      refetch();
      onClose();
      setLoading(false);
      Toast({
        type: 'success',
        message: res?.message,
      });
    } catch (ex) {
      setLoading(false);
      Toast({
        type: 'error',
        message: ex.message,
      });
    }
  };
  const { totalCount, rowsData } = useMemo(
    () => ({
      rowsData: dashboard_data.items.map(_ => [
        format(getDateObject(_.created_at), 'dd-MM-yyyy'),
        _?.orderNumber ?? '---',
        _.resellerId?.email,
        <OrderStatus order={_} />,
        <div css="text-align:center;">
          {_?.paymentProof ? (
            <ModalContainer
              btnComponent={({ onClick }) => (
                <Tooltip title={LangConverter('View Payment Proof', lang.value)}>
                  <Icon className="material-icons-outlined" onClick={onClick} iconName="visibility" />
                </Tooltip>
              )}
              title={LangConverter('Payment Proof', lang.value)}
              content={({ onClose: onClosePdf }) => <PdfViewer onClose={onClosePdf} pdf={_?.paymentProof} />}
              lg
            />
          ) : (
            '---'
          )}
        </div>,
        convertToCurrencyFormat(_.originalAmount),
        convertToCurrencyFormat(_.amount),
        getComissionAmount(_),
        !_?.hasComission
          ? '---'
          : _?.status === 'comission_paid'
          ? 'Comission Paid'
          : 'Not Paid' /* comission status */,
        // actionBtns(_),
      ]),
      totalCount: dashboard_data.totalItems,
    }),
    [dashboard_data],
  );

  const columnNames = [
    t(`Created at`),
    t(`Order Number`),
    t(`Reseller`),
    t(`status`),
    t(`Payment Proof`),
    t(`Original Amount`),
    t(`Discounted Amount`),
    t('Comission Amount'),
    t('Comission Status'),
    ``,
  ];

  return (
    <>
      <Paragraph>
        {t('Here you can')} <strong>{t('upload payment proof for multiple orders')}</strong>.{' '}
        {t(
          '. Select the date range to filter the orders and upload the payment proof for all the orders in the selected date range that',
        )}
        &nbsp;
        <strong>{t('does not have payment proof')}</strong>.{' '}
        {t('. Once submitted, the payment proof will be uploaded for all the orders not having payment proof.')}
      </Paragraph>
      <Form
        form={form}
        onSubmit={onSubmit}
        onTouched={_ => {
          setState(__ => ({ ...__, ..._ }));
        }}>
        <Grid
          xs={1}
          colGap={20}
          css={`
            align-items: center;
            justify-content: center;
            display: flex;
          `}>
          <Form.Item
            type="chooseFile"
            label="Upload File"
            name="paymentProof"
            pdf
            value={state?.paymentProof}
            rules={[{ required: true, message: t('File is required') }]}
            onChange={({ target }) => {
              setState(prev => ({ ...prev, paymentProof: target?.value }));
              form.setFieldsValue({ paymentProof: target?.value });
            }}>
            <Field />
          </Form.Item>
        </Grid>
        <Button
          sm
          disabled={loading}
          loading={loading}
          type="primary"
          htmlType="submit"
          css={`
            margin-top: 20px;
          `}>
          {t('Upload')}
        </Button>
      </Form>
      <TableLayout
        customFilterKey="date"
        onChangeFilters={filters => {
          setSearchQuery(_ => ({
            ..._,
            ...filters,
          }));
        }}
        currentPage={!searchQuery?.endDate ? 1 : searchQuery.page}
        totalCount={!searchQuery?.endDate ? 0 : totalCount}
        pageSize={!searchQuery?.endDate ? 10 : searchQuery.pageSize}>
        {!searchQuery?.endDate ? (
          <NoRecordFound>Select a date range to view the orders</NoRecordFound>
        ) : (
          <Table width={1200} loading={dashboard_loading} rowsData={rowsData} columnNames={columnNames} noPadding />
        )}
      </TableLayout>
    </>
  );
}

export default UploadBulkPaymentProof;
